/**
 * Icon slot: the painterly icon + keybind label + cooldown / GCD sweeps + charges + rank label +
 * pressed / usable / out-of-range / not-enough-resource / casting / queued states, the soft proc
 * pulse (`setProc`), the WoW-style spell-activation border (`setActivation`: a burst, then a
 * marching gold border; the layer is created on first use) and the transformed look
 * (`setTransformed`: a short icon swap pop and a corner mark).
 *
 * Per-frame contract: `render(view, realNow)` diffs against the last drawn state and touches
 * the DOM only on change. Sweeps write one interned `--p` string (kit/tokens.ts DEG), text is
 * keyed by integers (slotState.ts), classes flip only when a flag changes. No layout reads, no
 * allocations. `press(now)` adds the pressed class synchronously (call it from keydown), and
 * the CSS for `.pressed` has no transition, so the pressed look lands in the same frame.
 *
 * Probe contract (game/session.ts latency probe): the GCD layer always carries the marker class
 * `action-gcd` and toggles `on`; the root toggles `pressed`, `casting`, `queued`. The action bar
 * adds `action` to each root and `actionbar` to its container.
 */
import { iconUrl } from '../icons';
import { el } from '../dom';
import { initials } from '../format';
import { type TooltipProvider, attachAbilityTooltip } from '../tooltip/view';
import { type SlotUsable, type SlotView, cooldownKey, timerLabel } from './slotState';
import { DEG, type IconSizeName, intText, sweepIndex } from './tokens';

/** How long a press shows the pressed state. */
export const PRESS_FLASH_MS = 120;

export interface IconSlotOptions {
  size?: IconSizeName;
  /** Extra classes on the root (e.g. "action"). */
  className?: string;
  /** Keybind label (top-left). */
  keybind?: string;
  /** Primary-button pointerdown handler (press lands on the down stroke). Makes the root a button. */
  onPress?: (ev: PointerEvent) => void;
  /** Hover tooltip (re-evaluated while visible). */
  tooltip?: TooltipProvider;
}

const F_PRESSED = 1;
const F_CASTING = 2;
const F_QUEUED = 4;
const F_ONCD = 8;
const F_NORES = 16;
const F_OOR = 32;
const F_UNUSABLE = 64;
const F_EMPTY = 128;
const F_SWEEP = 256;
const F_GCD = 512;
const F_PROC = 1024;
const F_ACTIVATION = 2048;
const F_TRANSFORMED = 4096;

const FLAG_CLASS: ReadonlyArray<readonly [number, string, 'root' | 'sweep' | 'gcd']> = [
  [F_PRESSED, 'pressed', 'root'],
  [F_CASTING, 'casting', 'root'],
  [F_QUEUED, 'queued', 'root'],
  [F_ONCD, 'is-cd', 'root'],
  [F_NORES, 'is-nores', 'root'],
  [F_OOR, 'is-oor', 'root'],
  [F_UNUSABLE, 'is-unusable', 'root'],
  [F_EMPTY, 'is-empty', 'root'],
  [F_SWEEP, 'on', 'sweep'],
  [F_GCD, 'on', 'gcd'],
  [F_PROC, 'is-proc', 'root'],
  [F_ACTIVATION, 'is-activation', 'root'],
  [F_TRANSFORMED, 'is-transformed', 'root'],
];
/** Flags owned by setters (kept across `render`). */
const F_STICKY = F_EMPTY | F_PROC | F_ACTIVATION | F_TRANSFORMED;

const USABLE_FLAG: Record<SlotUsable, number> = { ok: 0, nores: F_NORES, oor: F_OOR, unusable: F_UNUSABLE };

export class IconSlot {
  readonly root: HTMLElement;
  private readonly img: HTMLImageElement;
  private readonly fallback: HTMLElement;
  private readonly sweep: HTMLElement;
  private readonly gcd: HTMLElement;
  private readonly cdText: HTMLElement;
  private readonly keyEl: HTMLElement;
  private readonly chargesEl: HTMLElement;
  private readonly rankEl: HTMLElement;
  private readonly size: IconSizeName;
  /** Spell-activation border layer (lazily created the first time the slot activates). */
  private glow: HTMLElement | null = null;
  /** The icon id currently shown (ability or aura id). */
  iconId: string | null = null;
  /** Real time (performance.now) of the last press. */
  pressedAt = -Infinity;
  private flags = 0;
  private sweepIdx = -1;
  private gcdIdx = -1;
  private gcdQIdx = -1;
  private cdKey = -1;
  private chargesShown = -1;

  constructor(parent: HTMLElement | null, opts: IconSlotOptions = {}) {
    this.size = opts.size ?? 'md';
    const tag = opts.onPress ? 'button' : 'div';
    this.root = el(tag, `hk-slot ${this.size}${opts.className ? ` ${opts.className}` : ''}`);
    if (tag === 'button') {
      (this.root as HTMLButtonElement).type = 'button';
      this.root.tabIndex = -1;
    }
    this.img = el('img', 'hk-slot-img', this.root);
    this.img.alt = '';
    this.img.draggable = false;
    this.img.decoding = 'async';
    this.fallback = el('div', 'hk-slot-fallback', this.root);
    el('div', 'hk-slot-tint', this.root); // +castux: WoW red out-of-range multiply layer (CSS .is-oor)
    this.sweep = el('div', 'hk-slot-sweep', this.root);
    this.gcd = el('div', 'hk-slot-gcd action-gcd', this.root);
    el('div', 'hk-slot-frame', this.root);
    this.cdText = el('div', 'hk-slot-cd hk-num', this.root);
    this.keyEl = el('div', 'hk-key', this.root, opts.keybind ?? '');
    this.chargesEl = el('div', 'hk-slot-charges hk-num', this.root);
    this.rankEl = el('div', 'hk-slot-rank hk-num', this.root);
    if (opts.onPress) {
      const handler = opts.onPress;
      this.root.addEventListener('pointerdown', (e) => {
        if (e.button !== 0) return;
        e.preventDefault();
        handler(e);
      });
    }
    if (opts.tooltip) attachAbilityTooltip(this.root, opts.tooltip);
    this.setIcon(null);
    parent?.appendChild(this.root);
  }

  /**
   * Show the icon for an ability / aura id (painterly PNG when there is one, else initials on
   * `color`). Call on change only (it touches attributes).
   */
  setIcon(id: string | null, fallback?: { name?: string; color?: string }): void {
    this.iconId = id;
    const url = id ? iconUrl(id, this.size === 'xs' || this.size === 'sm' ? 64 : 128) : null;
    if (url) {
      if (this.img.getAttribute('src') !== url) this.img.src = url;
      this.img.hidden = false;
      this.fallback.hidden = true;
    } else {
      this.img.removeAttribute('src');
      this.img.hidden = true;
      this.fallback.hidden = !id;
      this.fallback.textContent = id ? initials(fallback?.name ?? id) : '';
      this.fallback.style.setProperty('--school', fallback?.color ?? '#888');
    }
    this.setFlag(F_EMPTY, !id);
  }

  setKeybind(text: string): void {
    if (this.keyEl.textContent !== text) this.keyEl.textContent = text;
  }

  /** Small label at the bottom-left (rank / level / count, e.g. "Lv.3"). Empty hides it. */
  setRank(text: string): void {
    if (this.rankEl.textContent !== text) this.rankEl.textContent = text;
  }

  /** Proc / highlight glow (opacity-animated pseudo element). */
  setProc(on: boolean): void {
    this.setFlag(F_PROC, on);
  }

  /**
   * Spell activation (a proc empowers this button): burst + marching border. Compositor-only
   * animations (transform / opacity); the CSS restarts the burst each time the class goes on.
   */
  setActivation(on: boolean): void {
    if (on && !this.glow) this.glow = el('div', 'hk-slot-glow', this.root);
    this.setFlag(F_ACTIVATION, on);
  }

  /** The button shows a transformed form (icon swapped by the caller via setIcon). */
  setTransformed(on: boolean): void {
    this.setFlag(F_TRANSFORMED, on);
  }

  /** Pressed look right now (keydown / pointerdown), before the next frame or the sim. */
  press(now: number): void {
    this.pressedAt = now;
    this.setFlag(F_PRESSED, true);
  }

  /** Draw a frame. `realNow` is performance.now() (for the press flash). */
  render(v: SlotView, realNow: number): void {
    let f = this.flags & F_STICKY;
    if (realNow - this.pressedAt < PRESS_FLASH_MS) f |= F_PRESSED;
    if (v.casting) f |= F_CASTING;
    if (v.queued) f |= F_QUEUED;
    if (v.onCooldown) f |= F_ONCD;
    f |= USABLE_FLAG[v.usable];
    const si = sweepIndex(v.cdFrac);
    const gi = v.onCooldown ? 0 : sweepIndex(v.gcdFrac);
    if (si > 0) f |= F_SWEEP;
    if (gi > 0) f |= F_GCD;
    if (si !== this.sweepIdx) {
      this.sweepIdx = si;
      this.sweep.style.setProperty('--p', DEG[si]!);
    }
    if (gi !== this.gcdIdx) {
      this.gcdIdx = gi;
      this.gcd.style.setProperty('--p', DEG[gi]!);
    }
    // +castux: the spell-queue window (the GCD's last 400 ms) is the part of the sweep nearest 12 o'clock.
    const qi = gi > 0 ? Math.min(gi, sweepIndex(v.gcdQ ?? 0)) : 0;
    if (qi !== this.gcdQIdx) {
      this.gcdQIdx = qi;
      this.gcd.style.setProperty('--q', DEG[qi]!);
    }
    const ck = cooldownKey(v.cdMs);
    if (ck !== this.cdKey) {
      this.cdKey = ck;
      this.cdText.textContent = timerLabel(ck);
    }
    const ch = v.maxCharges > 1 ? v.charges : -2;
    if (ch !== this.chargesShown) {
      this.chargesShown = ch;
      this.chargesEl.textContent = ch >= 0 ? intText(ch) : '';
    }
    this.applyFlags(f);
  }

  /** Reset to an empty, idle slot. */
  clear(): void {
    this.setIcon(null);
    this.setRank('');
    this.setActivation(false);
    this.setTransformed(false);
    this.render(IDLE, -Infinity);
  }

  private setFlag(bit: number, on: boolean): void {
    this.applyFlags(on ? this.flags | bit : this.flags & ~bit);
  }

  private applyFlags(f: number): void {
    const diff = f ^ this.flags;
    if (!diff) return;
    this.flags = f;
    for (const [bit, cls, target] of FLAG_CLASS) {
      if (!(diff & bit)) continue;
      const e = target === 'root' ? this.root : target === 'sweep' ? this.sweep : this.gcd;
      e.classList.toggle(cls, !!(f & bit));
    }
  }
}

const IDLE: SlotView = { cdFrac: 0, cdMs: 0, onCooldown: false, gcdFrac: 0, charges: 0, maxCharges: 1, usable: 'ok', casting: false, queued: false };
